'use client';

/**
 * PinnedMediaCompact — mobile's media pin (design turn 14a/14b). Reuses
 * VideoBody/ArticleBody as-is (pinned-media-body.tsx) — they were already
 * compact-ish per the brief, so mobile's only change is the surrounding
 * chrome: the compact shell + header prev/dots/next cluster once the pin
 * holds >1 ref, and SwipeTrack in place of desktop's CardStack.
 *
 * Deliberately NO tap-to-expand here (unlike PinnedCourseCompact): media
 * bodies have no "compact vs full" split to defer to — VideoBody/ArticleBody
 * already surface their own Watch/Read-article links, so there is no
 * natural detail view to open in a Sheet. Building one would mean inventing
 * new UI not covered by the brief ("skip for media and note it" — see the
 * PR description). SwipeTrack is therefore mounted without an `onTap`.
 */
import { useState } from 'react';
import type { RAGReference } from '@/types/rag';
import { PinnedCardShell } from '../primitives/pinned-card-shell';
import { PinHeaderNav } from '../primitives/pin-header-nav';
import { SwipeTrack } from '../primitives/swipe-track';
import { MediaBody } from './pinned-media-body';

export interface PinnedMediaCompactProps {
  refs: RAGReference[];
  onDismiss: () => void;
  /** "Pinned by Sara" (lone pin) or the stack's kind-based label (14c) —
   *  see pin-history.ts's frontBadgeLabel. */
  badgeLabel: string;
}

export function PinnedMediaCompact({
  refs,
  onDismiss,
  badgeLabel,
}: PinnedMediaCompactProps) {
  const [index, setIndex] = useState(0);
  const clampedIndex = Math.min(index, refs.length - 1);

  return (
    <PinnedCardShell
      compact
      onDismiss={onDismiss}
      badgeLabel={badgeLabel}
      headerNav={
        refs.length > 1 ? (
          <PinHeaderNav
            index={clampedIndex}
            count={refs.length}
            onPrev={() => setIndex((i) => Math.max(0, i - 1))}
            onNext={() => setIndex((i) => Math.min(refs.length - 1, i + 1))}
          />
        ) : undefined
      }
    >
      {refs.length === 1 ? (
        <MediaBody refItem={refs[0]} />
      ) : (
        <SwipeTrack
          items={refs}
          index={clampedIndex}
          onIndexChange={setIndex}
          renderItem={(refItem) => <MediaBody refItem={refItem} />}
        />
      )}
    </PinnedCardShell>
  );
}

export default PinnedMediaCompact;
